import React, { useState, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronRight, ChevronLeft, Upload, AlertCircle, Save } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { RadioGroup } from './ui/RadioGroup';
import { Checkbox } from './ui/Checkbox';
// Types
interface FormData {
  // Step 1
  title: string;
  firstName: string;
  surname: string;
  otherNames: string;
  previousName: string;
  nationality: string;
  countryOfOrigin: string;
  identityNo: string;
  dob: string;
  gender: string;
  maritalStatus: string;
  residentialStatus: string;
  plotNumber: string;
  location: string;
  village: string;
  poBox: string;
  postalVillage: string;
  email: string;
  phoneHome: string;
  phoneWork: string;
  mobile1: string;
  mobile2: string;
  lodgingType: string[];
  // Step 2
  employerName: string;
  occupation: string;
  dateOfAppointment: string;
  lengthOfService: string;
  employerAddress: string;
  // Spouse
  spouseTitle: string;
  spouseFullName: string;
  spouseNationality: string;
  spouseIdentityNo: string;
  spouseDob: string;
  spouseGender: string;
  spouseOccupation: string;
  spouseEmployer: string;
  marriageType: string;
  // Step 3
  ref1Name: string;
  ref1Rel: string;
  ref1Gender: string;
  ref1Address: string;
  ref1Email: string;
  ref1Mobile: string;
  ref2Name: string;
  ref2Rel: string;
  ref2Gender: string;
  ref2Address: string;
  ref2Email: string;
  ref2Mobile: string;
  existingLoans: Array<{
    institution: string;
    type: string;
    instalment: string;
    balance: string;
    status: string;
  }>;
  // Step 4
  loanAmount: string;
  repaymentPeriod: string;
  grossSalary: string;
  adminFee: string;
  reason: string;
  referredBy: string;
  deductionMethod: string;
  stopOrderDate: string;
  // Step 5
  declarationMinor: boolean;
  declarationAdminOrder: boolean;
  declarationDebtReview: boolean;
  declarationTrue: boolean;
  declarationAuth: boolean;
  digitalSignature: string;
}
const INITIAL_DATA: FormData = {
  title: '',
  firstName: '',
  surname: '',
  otherNames: '',
  previousName: '',
  nationality: 'Motswana',
  countryOfOrigin: 'Botswana',
  identityNo: '',
  dob: '',
  gender: '',
  maritalStatus: '',
  residentialStatus: 'Citizen',
  plotNumber: '',
  location: '',
  village: '',
  poBox: '',
  postalVillage: '',
  email: '',
  phoneHome: '',
  phoneWork: '',
  mobile1: '',
  mobile2: '',
  lodgingType: [],
  employerName: '',
  occupation: '',
  dateOfAppointment: '',
  lengthOfService: '',
  employerAddress: '',
  spouseTitle: '',
  spouseFullName: '',
  spouseNationality: '',
  spouseIdentityNo: '',
  spouseDob: '',
  spouseGender: '',
  spouseOccupation: '',
  spouseEmployer: '',
  marriageType: '',
  ref1Name: '',
  ref1Rel: '',
  ref1Gender: '',
  ref1Address: '',
  ref1Email: '',
  ref1Mobile: '',
  ref2Name: '',
  ref2Rel: '',
  ref2Gender: '',
  ref2Address: '',
  ref2Email: '',
  ref2Mobile: '',
  existingLoans: [],
  loanAmount: '',
  repaymentPeriod: '',
  grossSalary: '',
  adminFee: '',
  reason: '',
  referredBy: '',
  deductionMethod: '',
  stopOrderDate: '',
  declarationMinor: false,
  declarationAdminOrder: false,
  declarationDebtReview: false,
  declarationTrue: false,
  declarationAuth: false,
  digitalSignature: ''
};
const STEPS = [{
  id: 1,
  title: 'Personal Details'
}, {
  id: 2,
  title: 'Employment & Spouse'
}, {
  id: 3,
  title: 'References'
}, {
  id: 4,
  title: 'Loan Details'
}, {
  id: 5,
  title: 'Uploads & Declaration'
}];
export function LoanApplicationWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleNext = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    if (currentStep < 5) setCurrentStep(prev => prev + 1);else handleSubmit();
  };
  const handleBack = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };
  const handleSubmit = () => {
    // Mock submission
    setTimeout(() => setIsSubmitted(true), 1500);
  };
  if (isSubmitted) {
    return <div className="max-w-2xl mx-auto py-16 px-4 text-center" data-id="element-183">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-184">
          <Check className="w-10 h-10 text-green-600" data-id="element-185" />
        </div>
        <h2 className="text-3xl font-heading font-bold text-navy mb-4" data-id="element-186">
          Application Received!
        </h2>
        <p className="text-gray-600 mb-8 text-lg" data-id="element-187">
          Thank you for applying with De Rien Financial Services. We have
          received your details and will contact you within 24 hours.
        </p>
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 text-left max-w-lg mx-auto mb-8" data-id="element-188">
          <h4 className="font-bold text-navy mb-2 flex items-center" data-id="element-189">
            <AlertCircle className="w-5 h-5 mr-2 text-blue-600" data-id="element-190" />
            What happens next?
          </h4>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-1" data-id="element-191">
            <li data-id="element-192">Our credit team will review your application.</li>
            <li data-id="element-193">You will receive a confirmation email shortly.</li>
            <li data-id="element-194">We may request additional documents if needed.</li>
          </ul>
        </div>
        <Button onClick={() => window.location.href = '/'} data-id="element-195">
          Return to Home
        </Button>
      </div>;
  }
  return <div className="max-w-5xl mx-auto px-4 py-8" data-id="element-196">
      {/* Progress Bar */}
      <div className="mb-10" data-id="element-197">
        <div className="flex justify-between mb-4" data-id="element-198">
          {STEPS.map(step => <div key={step.id} className="flex flex-col items-center relative z-10 w-1/5" data-id="element-199">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300 ${step.id <= currentStep ? 'bg-navy text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-200">
                {step.id < currentStep ? <Check className="w-5 h-5" data-id="element-201" /> : step.id}
              </div>
              <span className={`text-xs mt-2 font-medium text-center hidden sm:block ${step.id <= currentStep ? 'text-navy' : 'text-gray-400'}`} data-id="element-202">
                {step.title}
              </span>
            </div>)}
          {/* Background Line */}
          <div className="absolute top-4 left-0 w-full h-1 bg-gray-200 -z-0 hidden sm:block" data-id="element-203"></div>
          <div className="absolute top-4 left-0 h-1 bg-navy transition-all duration-500 -z-0 hidden sm:block" style={{
          width: `${(currentStep - 1) / (STEPS.length - 1) * 100}%`
        }} data-id="element-204"></div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8" data-id="element-205">
        {/* Main Form Area */}
        <div className="lg:col-span-2" data-id="element-206">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-207">
            <div className="mb-6 pb-4 border-b border-gray-100 flex justify-between items-center" data-id="element-208">
              <h2 className="text-2xl font-heading font-bold text-navy" data-id="element-209">
                {STEPS[currentStep - 1].title}
              </h2>
              <span className="text-sm text-gray-500" data-id="element-210">
                Step {currentStep} of 5
              </span>
            </div>

            {/* STEP 1: PERSONAL DETAILS */}
            {currentStep === 1 && <div className="space-y-6" data-id="element-211">
                <RadioGroup label="Title" name="title" options={[{
              label: 'Mr',
              value: 'Mr'
            }, {
              label: 'Mrs',
              value: 'Mrs'
            }, {
              label: 'Ms',
              value: 'Ms'
            }, {
              label: 'Dr',
              value: 'Dr'
            }, {
              label: 'Prof',
              value: 'Prof'
            }, {
              label: 'Rev',
              value: 'Rev'
            }, {
              label: 'Hon',
              value: 'Hon'
            }]} value={formData.title} onChange={val => updateField('title', val)} data-id="element-212" />

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-213">
                  <Input label="First Name" required value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} data-id="element-214" />
                  <Input label="Surname" required value={formData.surname} onChange={e => updateField('surname', e.target.value)} data-id="element-215" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-216">
                  <Input label="Other Names" value={formData.otherNames} onChange={e => updateField('otherNames', e.target.value)} data-id="element-217" />
                  <Input label="Previous Name" value={formData.previousName} onChange={e => updateField('previousName', e.target.value)} data-id="element-218" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-219">
                  <Input label="Nationality" value={formData.nationality} onChange={e => updateField('nationality', e.target.value)} data-id="element-220" />
                  <Input label="Country of Origin" value={formData.countryOfOrigin} onChange={e => updateField('countryOfOrigin', e.target.value)} data-id="element-221" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-222">
                  <Input label="Identity No (Omang/Passport)" required value={formData.identityNo} onChange={e => updateField('identityNo', e.target.value)} data-id="element-223" />
                  <Input label="Date of Birth" type="date" required value={formData.dob} onChange={e => updateField('dob', e.target.value)} data-id="element-224" />
                </div>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-225">
                  <RadioGroup label="Gender" name="gender" options={[{
                label: 'Male',
                value: 'Male'
              }, {
                label: 'Female',
                value: 'Female'
              }]} value={formData.gender} onChange={val => updateField('gender', val)} data-id="element-226" />
                  <RadioGroup label="Residential Status" name="resStatus" options={[{
                label: 'Citizen',
                value: 'Citizen'
              }, {
                label: 'Resident',
                value: 'Resident'
              }]} value={formData.residentialStatus} onChange={val => updateField('residentialStatus', val)} data-id="element-227" />
                </div>

                <RadioGroup label="Marital Status" name="maritalStatus" options={[{
              label: 'Single',
              value: 'Single'
            }, {
              label: 'Married',
              value: 'Married'
            }, {
              label: 'Divorced',
              value: 'Divorced'
            }, {
              label: 'Widowed',
              value: 'Widowed'
            }, {
              label: 'Separated',
              value: 'Separated'
            }]} value={formData.maritalStatus} onChange={val => updateField('maritalStatus', val)} data-id="element-228" />

                <div className="pt-4 border-t border-gray-100" data-id="element-229">
                  <h3 className="font-bold text-navy mb-4" data-id="element-230">Physical Address</h3>
                  <div className="grid sm:grid-cols-3 gap-4" data-id="element-231">
                    <Input label="Plot Number" value={formData.plotNumber} onChange={e => updateField('plotNumber', e.target.value)} data-id="element-232" />
                    <Input label="Location/Ward" value={formData.location} onChange={e => updateField('location', e.target.value)} data-id="element-233" />
                    <Input label="Village/Town" value={formData.village} onChange={e => updateField('village', e.target.value)} data-id="element-234" />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100" data-id="element-235">
                  <h3 className="font-bold text-navy mb-4" data-id="element-236">Contact Details</h3>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-237">
                    <Input label="Email Address" type="email" required value={formData.email} onChange={e => updateField('email', e.target.value)} data-id="element-238" />
                    <Input label="Mobile Number 1" type="tel" required value={formData.mobile1} onChange={e => updateField('mobile1', e.target.value)} data-id="element-239" />
                    <Input label="Mobile Number 2" type="tel" value={formData.mobile2} onChange={e => updateField('mobile2', e.target.value)} data-id="element-240" />
                    <Input label="Work Phone" type="tel" value={formData.phoneWork} onChange={e => updateField('phoneWork', e.target.value)} data-id="element-241" />
                  </div>
                </div>
              </div>}

            {/* STEP 2: EMPLOYMENT & SPOUSE */}
            {currentStep === 2 && <div className="space-y-6" data-id="element-242">
                <h3 className="font-bold text-navy border-b border-gray-100 pb-2" data-id="element-243">
                  Employment Details
                </h3>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-244">
                  <Input label="Employer Name" required value={formData.employerName} onChange={e => updateField('employerName', e.target.value)} data-id="element-245" />
                  <Input label="Occupation" required value={formData.occupation} onChange={e => updateField('occupation', e.target.value)} data-id="element-246" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-247">
                  <Input label="Date of Appointment" type="date" value={formData.dateOfAppointment} onChange={e => updateField('dateOfAppointment', e.target.value)} data-id="element-248" />
                  <Input label="Length of Service" placeholder="e.g. 5 years" value={formData.lengthOfService} onChange={e => updateField('lengthOfService', e.target.value)} data-id="element-249" />
                </div>
                <Input label="Employer Address" placeholder="Plot, Ward, Village" value={formData.employerAddress} onChange={e => updateField('employerAddress', e.target.value)} data-id="element-250" />

                {formData.maritalStatus === 'Married' && <div className="mt-8 pt-6 border-t border-gray-200 animate-in fade-in slide-in-from-top-4" data-id="element-251">
                    <h3 className="font-bold text-navy border-b border-gray-100 pb-2 mb-4" data-id="element-252">
                      Spouse Details
                    </h3>
                    <div className="space-y-4" data-id="element-253">
                      <RadioGroup label="Spouse Title" name="spouseTitle" options={[{
                  label: 'Mr',
                  value: 'Mr'
                }, {
                  label: 'Mrs',
                  value: 'Mrs'
                }, {
                  label: 'Ms',
                  value: 'Ms'
                }]} value={formData.spouseTitle} onChange={val => updateField('spouseTitle', val)} data-id="element-254" />
                      <Input label="Full Names of Spouse" value={formData.spouseFullName} onChange={e => updateField('spouseFullName', e.target.value)} data-id="element-255" />
                      <div className="grid sm:grid-cols-2 gap-4" data-id="element-256">
                        <Input label="Spouse Identity No" value={formData.spouseIdentityNo} onChange={e => updateField('spouseIdentityNo', e.target.value)} data-id="element-257" />
                        <Input label="Spouse DOB" type="date" value={formData.spouseDob} onChange={e => updateField('spouseDob', e.target.value)} data-id="element-258" />
                      </div>
                      <RadioGroup label="Marriage Type" name="marriageType" options={[{
                  label: 'In Community of Property',
                  value: 'In Community'
                }, {
                  label: 'Out of Community',
                  value: 'Out of Community'
                }]} value={formData.marriageType} onChange={val => updateField('marriageType', val)} data-id="element-259" />
                    </div>
                  </div>}
              </div>}

            {/* STEP 3: REFERENCES */}
            {currentStep === 3 && <div className="space-y-8" data-id="element-260">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-261">
                  <h3 className="font-bold text-navy mb-4" data-id="element-262">
                    Referee 1 (Required)
                  </h3>
                  <div className="space-y-4" data-id="element-263">
                    <div className="grid sm:grid-cols-2 gap-4" data-id="element-264">
                      <Input label="Full Name" required value={formData.ref1Name} onChange={e => updateField('ref1Name', e.target.value)} data-id="element-265" />
                      <Input label="Relationship" required value={formData.ref1Rel} onChange={e => updateField('ref1Rel', e.target.value)} data-id="element-266" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4" data-id="element-267">
                      <Input label="Mobile Number" type="tel" required value={formData.ref1Mobile} onChange={e => updateField('ref1Mobile', e.target.value)} data-id="element-268" />
                      <Input label="Email Address" type="email" value={formData.ref1Email} onChange={e => updateField('ref1Email', e.target.value)} data-id="element-269" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-270">
                  <h3 className="font-bold text-navy mb-4" data-id="element-271">
                    Referee 2 (Required)
                  </h3>
                  <div className="space-y-4" data-id="element-272">
                    <div className="grid sm:grid-cols-2 gap-4" data-id="element-273">
                      <Input label="Full Name" required value={formData.ref2Name} onChange={e => updateField('ref2Name', e.target.value)} data-id="element-274" />
                      <Input label="Relationship" required value={formData.ref2Rel} onChange={e => updateField('ref2Rel', e.target.value)} data-id="element-275" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4" data-id="element-276">
                      <Input label="Mobile Number" type="tel" required value={formData.ref2Mobile} onChange={e => updateField('ref2Mobile', e.target.value)} data-id="element-277" />
                      <Input label="Email Address" type="email" value={formData.ref2Email} onChange={e => updateField('ref2Email', e.target.value)} data-id="element-278" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* STEP 4: LOAN DETAILS */}
            {currentStep === 4 && <div className="space-y-6" data-id="element-279">
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-280">
                  <Input label="Loan Amount Requested (BWP)" type="number" required placeholder="e.g. 50000" value={formData.loanAmount} onChange={e => updateField('loanAmount', e.target.value)} data-id="element-281" />
                  <Select label="Repayment Period" required options={Array.from({
                length: 36
              }, (_, i) => ({
                label: `${i + 1} Months`,
                value: `${i + 1}`
              }))} value={formData.repaymentPeriod} onChange={e => updateField('repaymentPeriod', e.target.value)} data-id="element-282" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-283">
                  <Input label="Gross Salary (BWP)" type="number" required value={formData.grossSalary} onChange={e => updateField('grossSalary', e.target.value)} data-id="element-284" />
                  <Input label="Admin Fee (Optional)" type="number" value={formData.adminFee} onChange={e => updateField('adminFee', e.target.value)} data-id="element-285" />
                </div>

                <div data-id="element-286">
                  <label className="block text-sm font-medium text-navy mb-1.5" data-id="element-287">
                    Reason for Loan Required{' '}
                    <span className="text-red-500" data-id="element-288">*</span>
                  </label>
                  <textarea className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-navy focus:outline-none" rows={3} value={formData.reason} onChange={e => updateField('reason', e.target.value)} data-id="element-289"></textarea>
                </div>

                <div className="pt-4 border-t border-gray-100" data-id="element-290">
                  <h3 className="font-bold text-navy mb-4" data-id="element-291">Deduction Method</h3>
                  <RadioGroup name="deductionMethod" options={[{
                label: 'Speed Point',
                value: 'Speed Point'
              }, {
                label: 'Over The Counter',
                value: 'Over The Counter'
              }, {
                label: 'Online Banking',
                value: 'Online Banking'
              }, {
                label: 'Source Pay (Pay Slip)',
                value: 'Source Pay'
              }]} value={formData.deductionMethod} onChange={val => updateField('deductionMethod', val)} data-id="element-292" />
                  <div className="mt-4 max-w-xs" data-id="element-293">
                    <Input label="Date of Stop Order" type="date" value={formData.stopOrderDate} onChange={e => updateField('stopOrderDate', e.target.value)} data-id="element-294" />
                  </div>
                </div>
              </div>}

            {/* STEP 5: UPLOADS & DECLARATION */}
            {currentStep === 5 && <div className="space-y-8" data-id="element-295">
                <div data-id="element-296">
                  <h3 className="font-bold text-navy mb-4" data-id="element-297">
                    Required Documents
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-298">
                    {['Omang / Passport', 'Confirmation Letter of Employment', '3 Months Bank Statements', 'Latest Payslip', 'Work & Residence Permit (if applicable)'].map((doc, idx) => <div key={idx} className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-navy transition-colors cursor-pointer bg-gray-50" data-id="element-299">
                        <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" data-id="element-300" />
                        <span className="text-sm text-gray-600 block font-medium" data-id="element-301">
                          {doc}
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-302">
                          Click to upload PDF or Image
                        </span>
                      </div>)}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800" data-id="element-303">
                  <h4 className="font-bold mb-2 flex items-center" data-id="element-304">
                    <AlertCircle className="w-4 h-4 mr-2" data-id="element-305" /> Important Notices
                  </h4>
                  <ul className="list-disc list-inside space-y-1" data-id="element-306">
                    <li data-id="element-307">
                      <strong data-id="element-308">Cooling-off Period:</strong> You may cancel within
                      2 days.
                    </li>
                    <li data-id="element-309">
                      <strong data-id="element-310">Cost of Credit:</strong> Prorated cost of credit
                      P50. No termination fee.
                    </li>
                    <li data-id="element-311">
                      <strong data-id="element-312">Penalty:</strong> 5% penalty on late payments.
                    </li>
                  </ul>
                </div>

                <div className="space-y-3" data-id="element-313">
                  <h3 className="font-bold text-navy" data-id="element-314">Declaration</h3>
                  <Checkbox label="I am not a minor" required checked={formData.declarationMinor} onChange={e => updateField('declarationMinor', e.target.checked)} data-id="element-315" />
                  <Checkbox label="I am not subject to an administration order" required checked={formData.declarationAdminOrder} onChange={e => updateField('declarationAdminOrder', e.target.checked)} data-id="element-316" />
                  <Checkbox label="I do not have pending debt restructuring/alleviation" required checked={formData.declarationDebtReview} onChange={e => updateField('declarationDebtReview', e.target.checked)} data-id="element-317" />
                  <Checkbox label="I declare that the information provided is true and correct" required checked={formData.declarationTrue} onChange={e => updateField('declarationTrue', e.target.checked)} data-id="element-318" />
                  <Checkbox label="I authorize admin fee + installments deductions" required checked={formData.declarationAuth} onChange={e => updateField('declarationAuth', e.target.checked)} data-id="element-319" />
                </div>

                <div data-id="element-320">
                  <Input label="Digital Signature (Type Full Name)" required placeholder="e.g. John Doe" value={formData.digitalSignature} onChange={e => updateField('digitalSignature', e.target.value)} helperText={`Date: ${new Date().toLocaleDateString()}`} data-id="element-321" />
                </div>
              </div>}

            {/* Navigation Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-100 flex justify-between items-center" data-id="element-322">
              <Button variant="ghost" onClick={handleBack} disabled={currentStep === 1} className={currentStep === 1 ? 'invisible' : ''} data-id="element-323">
                <ChevronLeft className="w-4 h-4 mr-2" data-id="element-324" /> Back
              </Button>

              <div className="flex gap-3" data-id="element-325">
                <Button variant="outline" className="hidden sm:flex" onClick={() => alert('Draft saved!')} data-id="element-326">
                  <Save className="w-4 h-4 mr-2" data-id="element-327" /> Save Draft
                </Button>
                <Button onClick={handleNext} className="min-w-[120px]" data-id="element-328">
                  {currentStep === 5 ? 'Submit Application' : 'Next Step'}
                  {currentStep !== 5 && <ChevronRight className="w-4 h-4 ml-2" data-id="element-329" />}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1" data-id="element-330">
          <div className="sticky top-24 space-y-6" data-id="element-331">
            <div className="bg-navy text-white rounded-xl p-6 shadow-lg" data-id="element-332">
              <h3 className="font-bold text-xl mb-2" data-id="element-333">Need Help?</h3>
              <p className="text-navy-light text-sm mb-6" data-id="element-334">
                Our loan advisors are ready to assist you with your application.
              </p>

              <div className="space-y-4" data-id="element-335">
                <div className="flex items-center" data-id="element-336">
                  <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center mr-3" data-id="element-337">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-338">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" data-id="element-339"></path>
                    </svg>
                  </div>
                  <div data-id="element-340">
                    <p className="text-xs text-gray-300" data-id="element-341">Call Us</p>
                    <p className="font-bold" data-id="element-342">+267 74 833 041</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-343">
                  <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center mr-3" data-id="element-344">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-345">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-346"></path>
                    </svg>
                  </div>
                  <div data-id="element-347">
                    <p className="text-xs text-gray-300" data-id="element-348">Email Us</p>
                    <p className="font-bold" data-id="element-349">derien@derien.co.bw</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-350">
              <h4 className="font-bold text-navy mb-3" data-id="element-351">Why Apply Online?</h4>
              <ul className="space-y-3 text-sm text-gray-600" data-id="element-352">
                <li className="flex items-start" data-id="element-353">
                  <Check className="w-4 h-4 text-gold mr-2 mt-0.5" data-id="element-354" /> Fast
                  24-hour turnaround
                </li>
                <li className="flex items-start" data-id="element-355">
                  <Check className="w-4 h-4 text-gold mr-2 mt-0.5" data-id="element-356" /> Secure
                  document upload
                </li>
                <li className="flex items-start" data-id="element-357">
                  <Check className="w-4 h-4 text-gold mr-2 mt-0.5" data-id="element-358" /> Track
                  application status
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
}